/**
 * CANTIDAD Y COSTO UNITARIO DE UN ITEM DE FACTURA OCR, ANTES DE CONFIRMAR (FV-T5, pedido de Josse 30-jul).
 *
 * POR QUE: el OCR se equivoca y hoy nadie ve el numero antes de que entre al inventario (caso real 29-jul: "Coca Cola
 * 1.4" quedo con costo de 3 pesos). La revision de la factura deja corregir la cantidad o el costo unitario.
 *
 * REGLA DURA: EL SUBTOTAL MANDA. Es lo que cobro el proveedor y no se toca. Cambiar la cantidad recalcula el costo
 * (subtotal / cantidad) y cambiar el costo recalcula la cantidad (subtotal / costo): los tres nunca se contradicen.
 *
 * EN LAS UNIDADES DE LA FACTURA: se corrige cantidad_factura (lo que dice el papel: 4 KG). La API, con recalc, hace
 * cantidad = cantidad_factura * factor_empaque y precio_unitario = total_linea / cantidad (FV-T2, NexoAPI
 * FacturaCompraItemUpdateDto.Recalc). El n8n /purchase/match lee factura_compra_items.cantidad al crear la ENTRADA,
 * asi que la correccion guardada ANTES del match es la que entra al inventario.
 *
 * Sin DOM: lo prueba scripts/check-ajuste-item-factura.mjs.
 */

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const pick = (o, ...k) => { for (const c of k) if (o && o[c] !== undefined && o[c] !== null) return o[c]; return null; };

/** Un numero escrito por una persona en Colombia: "12.500,5" o "12500.5" o "4". null si no es > 0. */
export function leerNumero(texto) {
  let s = String(texto ?? '').trim().replace(/\s|\$/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Los items de la factura (GET /compras/{id}, ya adaptados o crudos) por id, en unidades de la factura.
 * Un item sin subtotal o sin cantidad no se puede ajustar (ajustable = false): se muestra, no se edita.
 */
export function itemsPorId(items) {
  const m = new Map();
  for (const it of Array.isArray(items) ? items : []) {
    const id = String(pick(it, 'id', 'item_id', 'Id') ?? '');
    if (!id) continue;
    const subtotal = num(pick(it, 'subtotal', 'totalLinea', 'total_linea'));
    const factor = num(pick(it, 'factor_empaque', 'factorEmpaque')) ?? 1;
    const cantInv = num(pick(it, 'cantidad', 'Cantidad'));
    // Sin cantidad_factura (facturas viejas), la de inventario dividida por el factor.
    const cantidad = num(pick(it, 'cantidad_factura', 'cantidadFactura')) ?? (cantInv !== null ? cantInv / factor : null);
    const unidad = String(pick(it, 'unidad_factura', 'unidadFactura', 'unidad') ?? '');
    m.set(id, {
      id, subtotal, factor, unidad, cantidad,
      costo: subtotal !== null && cantidad > 0 ? subtotal / cantidad : null,
      ajustable: subtotal !== null && subtotal > 0 && cantidad > 0
    });
  }
  return m;
}

/** Cambia la cantidad: el costo sale del subtotal. null si la cantidad no sirve. */
export function porCantidad(item, cantidad) {
  if (!item?.ajustable || !(cantidad > 0)) return null;
  return { ...item, cantidad, costo: item.subtotal / cantidad };
}

/** Cambia el costo unitario: la cantidad sale del subtotal. null si el costo no sirve. */
export function porCosto(item, costo) {
  if (!item?.ajustable || !(costo > 0)) return null;
  return { ...item, cantidad: item.subtotal / costo, costo };
}

/**
 * EL RUBRO DEL PROVEEDOR EN LA REVISION (FV-T5 punto 1): "si el proveedor ya tiene rubro (proveedores.rubro_id) NO
 * se pregunta: se muestra y listo". Lo sabe la API cuando manda proveedorRubroId en el header del GET /compras/{id}
 * (pedido a A, 13-oct); antes de eso solo hay el recuerdo de este navegador (cache de ComprasView), que NO cuenta como
 * "ya tiene": con el cache se preselecciona, pero se sigue preguntando.
 * @returns {{ actual: string|null, nombre: string|null, delProveedor: boolean }}
 */
export function rubroDeRevision(header, cacheado, opciones) {
  const deApi = pick(header, 'proveedor_rubro_id', 'proveedorRubroId');
  const id = deApi ?? cacheado ?? null;
  const op = (Array.isArray(opciones) ? opciones : []).find((o) => String(o.id) === String(id));
  return {
    actual: id === null || id === undefined ? null : String(id),
    nombre: pick(header, 'proveedor_rubro_nombre', 'proveedorRubroNombre') ?? op?.nombre ?? null,
    delProveedor: deApi !== null && deApi !== undefined
  };
}

/** Cantidad en unidades de inventario (la que entra al stock). */
export const cantidadInventario = (item) => (item && item.cantidad !== null ? item.cantidad * (item.factor || 1) : null);

/**
 * Lo que hay que guardar antes del match: solo los items cuya cantidad cambio. Va cantidad_factura + recalc y NADA
 * de match_status (pasar a MATCH por PATCH crearia la ENTRADA antes de tiempo). 6 decimales: lo que acepta la API.
 * @returns {Array<{ id:string, cantidad_factura:number, factor_empaque:number, recalc:true }>}
 */
export function cambiosParaGuardar(originales, editados) {
  const out = [];
  for (const [id, e] of editados || new Map()) {
    const o = originales.get(id);
    if (!o || !e || !(e.cantidad > 0)) continue;
    if (Math.abs(e.cantidad - o.cantidad) < 1e-6) continue;
    // factor_empaque viaja SIEMPRE: con recalc, la API responde 400 si el item no lo tiene guardado
    // (ComprasService.cs, "recalc con empaque requiere cantidadFactura y factorEmpaque").
    out.push({ id, cantidad_factura: Math.round(e.cantidad * 1e6) / 1e6, factor_empaque: o.factor || 1, recalc: true });
  }
  return out;
}
